import React, { useState, useMemo, useCallback } from "react";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Stack,
  Button,
  Menu,
  MenuItem,
  List,
  Skeleton,
} from "@mui/material";
import { Close, KeyboardArrowDown } from "@mui/icons-material";
import { useCompanyPageStore } from "../../../../store/companyPageStore";
import { lightScrollbarStyle } from "../../../../styles/lightScrollbarStyle";
import { ViewByOption, sortStrategies, groupStrategies } from "./utils";
import { VIEW_BY_OPTIONS } from "./constants";
import { CollapsibleGroup } from "./CollapsibleGroup";
import { CompanyListItem } from "./CompanyListItem";
import { SearchField } from "./SearchField";

export const OrganizationView: React.FC = React.memo(() => {
  const organizationViewOpen = useCompanyPageStore(
    (state) => state.organizationViewOpen
  );

  const setOrganizationViewOpen = useCompanyPageStore(
    (state) => state.setOrganizationViewOpen
  );
  const companyOrgTree = useCompanyPageStore((state) => state.companyOrgTree);
  const orgTreeLoading = useCompanyPageStore(
    (state) => state.loadingStates.orgTree
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [viewBy, setViewBy] = useState<ViewByOption>(VIEW_BY_OPTIONS.NAME);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const sortedCompanies = React.useMemo(() => {
    const strategy = sortStrategies[viewBy];
    return strategy.sort(companyOrgTree);
  }, [companyOrgTree, viewBy]);

  const filteredCompanies = useMemo(() => {
    if (!searchTerm.trim()) return sortedCompanies;

    const searchLower = searchTerm.toLowerCase();
    return sortedCompanies.filter((company) =>
      company.name.toLowerCase().includes(searchLower)
    );
  }, [sortedCompanies, searchTerm]);

  const groupedData = React.useMemo(() => {
    const strategy = groupStrategies[viewBy];

    if (!strategy.needsGrouping) return null;

    return strategy.group(filteredCompanies);
  }, [filteredCompanies, viewBy]);

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setOrganizationViewOpen(false);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewBySelect = (value: ViewByOption) => {
    setViewBy(value);
    handleMenuClose();
  };

  const toggleGroupExpansion = (key: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }

      return newSet;
    });
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  return (
    <Paper
      elevation={8}
      sx={{
        position: "absolute",
        top: 2,
        right: 2,
        width: 300,
        height: "calc(100% - 4px)",
        overflow: "hidden",
        borderRadius: 2,
        transform: organizationViewOpen
          ? "translateX(0)"
          : "translateX(calc(100% + 8px))",
        transition: "transform 200ms ease-out",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
        >
          <Typography variant="h6" id="organization-view-modal">
            Organization View
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          bgcolor="action.hover"
          px={2}
          py={1}
          mb={1}
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              View by:
            </Typography>
            <Button
              size="small"
              endIcon={<KeyboardArrowDown />}
              onClick={handleMenuClick}
              sx={{
                textTransform: "none",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {viewBy}
            </Button>
          </Stack>
        </Stack>

        <Box px={2} pb={1}>
          <SearchField
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder="Search companies..."
          />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {Object.values(VIEW_BY_OPTIONS).map((option) => (
            <MenuItem key={option} onClick={() => handleViewBySelect(option)}>
              {option}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box flexGrow={1} overflow="auto" sx={lightScrollbarStyle}>
        {orgTreeLoading ? (
          // Show skeleton while loading
          <Box px={2} py={1}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} mb={1}>
                <Skeleton variant="text" height={48} />
              </Box>
            ))}
          </Box>
        ) : filteredCompanies.length === 0 && searchTerm.trim() ? (
          // No results state
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No results found for "{searchTerm}"
            </Typography>
          </Box>
        ) : (
          // Existing list rendering
          <List disablePadding>
            {groupedData
              ? groupedData.map(({ key, companies, icon }) => (
                  <CollapsibleGroup
                    key={key}
                    groupKey={key}
                    icon={icon}
                    companies={companies}
                    isExpanded={expandedGroups.has(key)}
                    onToggle={toggleGroupExpansion}
                  />
                ))
              : filteredCompanies.map((company) => (
                  <CompanyListItem key={company.hgId} company={company} />
                ))}
          </List>
        )}
      </Box>
    </Paper>
  );
});

OrganizationView.displayName = "OrganizationView";
